import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Check, Upload, X, ArrowLeft, ArrowRight, Loader2, Star } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { adminApi, mediaUrl } from "./adminApi";

const TYPES = ["Appartement", "Maison", "Terrain", "Immeuble", "Autre"];
const COLLECTIONS = ["Habitat", "Signature", "Prestige"];

export default function SubmissionEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const galleryRef = useRef();

  useEffect(() => { adminApi.getSubmission(id).then((d) => setS({ ...d, images: d.images || [], highlights: d.highlights || [] })); }, [id]);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const addImages = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try { const urls = []; for (const file of files) { const r = await adminApi.upload(file); urls.push(mediaUrl(r.url)); } set("images", [...s.images, ...urls]); } catch (ex) { setErr(ex.message); }
    setUploading(false);
  };
  const removeImg = (i) => set("images", s.images.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const arr = [...s.images]; const j = i + dir; if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; set("images", arr);
  };

  const payload = () => ({
    ...s,
    price: Number(s.price) || 0, surface: Number(s.surface) || 0, land: Number(s.land) || 0,
    rooms: Number(s.rooms) || 0, bedrooms: Number(s.bedrooms) || 0,
    cover: s.images?.[0] || s.cover || null,
    highlights: Array.isArray(s.highlights) ? s.highlights : String(s.highlights || "").split(",").map((x) => x.trim()).filter(Boolean),
  });

  const save = async () => { setSaving(true); setErr(""); try { await adminApi.updateSubmission(id, payload()); } catch (ex) { setErr(ex.message); } finally { setSaving(false); } };
  const publish = async () => {
    setPublishing(true); setErr("");
    try { await adminApi.updateSubmission(id, payload()); await adminApi.publishSubmission(id); nav("/admin/submissions"); }
    catch (ex) { setErr(ex.message); setPublishing(false); }
  };

  const inp = "w-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#C19A6B] transition-colors";
  const lbl = "block text-xs uppercase tracking-wider text-zinc-400 mb-1.5";

  if (!s) return <AdminShell title="Chargement…"><p className="text-zinc-500 text-sm">Chargement…</p></AdminShell>;

  return (
    <AdminShell
      title="Modifier avant publication"
      action={<button onClick={() => nav("/admin/submissions")} className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-white border border-white/10 px-4 py-2.5"><ArrowLeft className="w-4 h-4" /> Retour</button>}
    >
      {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3 mb-6" data-testid="sub-edit-error">{err}</div>}

      {/* Propriétaire (lecture seule) */}
      <div className="bg-[#111114] border border-white/10 p-4 mb-8 text-sm text-zinc-400 flex flex-wrap gap-x-8 gap-y-1">
        <span><span className="text-zinc-600">Propriétaire :</span> {s.owner_prenom} {s.owner_nom}</span>
        <span><span className="text-zinc-600">Tél :</span> {s.owner_telephone}</span>
        <span><span className="text-zinc-600">E-mail :</span> {s.owner_email || "—"}</span>
        {s.documents?.length > 0 && <span><span className="text-zinc-600">Documents :</span> {s.documents.length}</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="submission-edit-form">
        <div className="lg:col-span-2 space-y-5">
          <div><label className={lbl}>Titre (SEO)</label><input value={s.title || ""} onChange={(e) => set("title", e.target.value)} className={inp} data-testid="sub-title" /></div>
          <div><label className={lbl}>Résumé (SEO)</label><input value={s.summary || ""} onChange={(e) => set("summary", e.target.value)} className={inp} data-testid="sub-summary" /></div>
          <div><label className={lbl}>Description</label><textarea rows={6} value={s.description || ""} onChange={(e) => set("description", e.target.value)} className={inp} data-testid="sub-desc" /></div>
          <div><label className={lbl}>Points forts (séparés par des virgules)</label><input value={Array.isArray(s.highlights) ? s.highlights.join(", ") : s.highlights} onChange={(e) => set("highlights", e.target.value.split(",").map((x) => x.trim()))} className={inp} /></div>

          <div>
            <label className={lbl}>Photos (la 1re est la couverture — réorganisez si besoin)</label>
            <div className="flex flex-wrap gap-3">
              {s.images.map((src, i) => (
                <div key={i} className="relative w-32 h-24 group border border-white/10">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute top-1 left-1 bg-[#C19A6B] text-white text-[9px] px-1 flex items-center gap-0.5"><Star className="w-2.5 h-2.5" /> COUV.</span>}
                  <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => move(i, -1)} className="p-1 text-white hover:text-[#C19A6B]"><ArrowLeft className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => removeImg(i)} className="p-1 text-white hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => move(i, 1)} className="p-1 text-white hover:text-[#C19A6B]"><ArrowRight className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => galleryRef.current.click()} className="w-32 h-24 border border-dashed border-white/20 flex items-center justify-center text-zinc-500 hover:border-[#C19A6B]/40" data-testid="sub-add-photos"><Upload className="w-5 h-5" /></button>
              <input ref={galleryRef} type="file" accept="image/*" multiple onChange={addImages} className="hidden" />
            </div>
            {uploading && <p className="text-xs text-[#C19A6B] flex items-center gap-2 mt-2"><Loader2 className="w-3 h-3 animate-spin" /> Upload…</p>}
          </div>
        </div>

        <div className="space-y-5">
          <div><label className={lbl}>Prix (€)</label><input type="number" value={s.price || ""} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="sub-price" /></div>
          <div><label className={lbl}>Ville</label><input value={s.city || ""} onChange={(e) => set("city", e.target.value)} className={inp} data-testid="sub-city" /></div>
          <div><label className={lbl}>Code postal</label><input value={s.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Type</label><select value={s.type || "Maison"} onChange={(e) => set("type", e.target.value)} className={inp}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label className={lbl}>Collection</label><select value={s.collection || "Habitat"} onChange={(e) => set("collection", e.target.value)} className={inp} data-testid="sub-collection">{COLLECTIONS.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Surface</label><input type="number" value={s.surface || ""} onChange={(e) => set("surface", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Terrain</label><input type="number" value={s.land || ""} onChange={(e) => set("land", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Pièces</label><input type="number" value={s.rooms || ""} onChange={(e) => set("rooms", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Chambres</label><input type="number" value={s.bedrooms || ""} onChange={(e) => set("bedrooms", e.target.value)} className={inp} /></div>
          </div>
          <div><label className={lbl}>DPE</label><input value={s.dpe || ""} onChange={(e) => set("dpe", e.target.value)} className={inp} placeholder="A à G" /></div>
          <label className="flex items-center gap-3 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={!!s.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#C19A6B]" /> Mettre à la une
          </label>

          <div className="space-y-3 pt-2">
            <button onClick={save} disabled={saving || publishing} className="w-full flex items-center justify-center gap-2 border border-white/20 hover:bg-white/5 text-white font-medium uppercase tracking-wider text-sm py-3 transition-colors disabled:opacity-60" data-testid="sub-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
            </button>
            <button onClick={publish} disabled={publishing || saving} className="w-full flex items-center justify-center gap-2 bg-emerald-500/90 hover:bg-emerald-500 text-white font-medium uppercase tracking-wider text-sm py-3.5 transition-colors disabled:opacity-60" data-testid="sub-publish">
              {publishing ? <><Loader2 className="w-4 h-4 animate-spin" /> Publication…</> : <><Check className="w-4 h-4" /> Publier</>}
            </button>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
